'use client'

import { MessageCircle } from 'lucide-react'
import Image from 'next/image'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { openSupportChat } from '@/lib/support-chat'
import mailBoxImg from '@/public/img/mailbox.svg'

interface NewAttendanceDialogProps {
  children: ReactNode
  className: string
  supportEmail: string | null
}

export function NewAttendanceDialog({
  children,
  className,
  supportEmail,
}: NewAttendanceDialogProps) {
  const [isOpen, setIsOpen] = useState(false)

  function handleOpenChat() {
    if (openSupportChat()) {
      setIsOpen(false)
      return
    }

    toast.error(
      'O chat não está disponível no momento. Tente novamente em instantes ou fale conosco por e-mail.'
    )
  }

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger asChild>
        <button className={className} type="button">
          {children}
        </button>
      </DialogTrigger>

      <DialogContent className="w-full max-w-150 py-4">
        <DialogHeader className="flex items-center space-y-6">
          <div className="flex flex-wrap items-center gap-x-16 gap-y-6 lg:flex-nowrap">
            <div className="space-y-2">
              <DialogTitle className="text-2xl text-[#333333]">
                Fale com nossa Equipe de Suporte
              </DialogTitle>
              <DialogDescription className="font-medium text-gray-500 text-sm">
                Estamos a disposição para lhe ajudar no que precisar.
              </DialogDescription>
            </div>
            <Image alt="" height={179.77} src={mailBoxImg} width={210} />
          </div>
        </DialogHeader>

        <div className="-mt-8 flex flex-wrap items-center gap-x-4 gap-y-4 lg:flex-nowrap">
          <button
            className="flex h-[7.0175rem] w-full flex-1 cursor-pointer flex-col items-center justify-center gap-1 rounded-md bg-[#1036F2] text-center font-semibold text-white text-xl transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none"
            onClick={handleOpenChat}
            type="button"
          >
            <MessageCircle aria-hidden="true" className="size-6" />
            Converse agora
          </button>

          {supportEmail ? (
            <a
              className="flex h-[7.0175rem] w-full flex-1 flex-col items-center justify-center rounded-md bg-[#3496fa] text-center font-semibold text-white text-xl transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none"
              href={`mailto:${supportEmail}`}
              rel="noreferrer"
              target="_blank"
            >
              E-mail
              <small className="block font-normal text-sm">
                {supportEmail}
              </small>
            </a>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  )
}
